
/**
 * IBKUL Reusable Size Guide
 *
 * Features:
 * - Size Chart / How to Measure tabs
 * - IN / CM measurement conversion
 * - Fraction and range conversion
 * - Dynamic How to Measure formatting
 * - Supports Girls, Boys, Women, Men
 *
 * Modal open/close handled by popup.js
 * Source measurements are stored in inches.
 */

(function () {
  'use strict';

  /* ==========================================
     1. MEASUREMENT CONVERSION
     ========================================== */

  const fractions = {
    '¼': 0.25,
    '½': 0.5,
    '¾': 0.75,
    '⅛': 0.125,
    '⅜': 0.375,
    '⅝': 0.625,
    '⅞': 0.875
  };

  function numeric(value) {
    let str = String(value).trim().replace(/,/g, '.');
    let fraction = 0;

    for (const [symbol, amount] of Object.entries(fractions)) {
      if (str.includes(symbol)) {
        fraction += amount;
        str = str.replace(symbol, '');
      }
    }

    const match = str.match(
      /^(-?\d+(?:\.\d+)?)\s*(?:\/\s*(\d+))?$/
    );

    if (!match) return NaN;

    const whole = Number(match[1]);

    return (
      (match[2] ? whole / Number(match[2]) : whole)
      + fraction
    );
  }

  function toCm(source) {
    return String(source)
      .split(/\s*[–—-]\s*/)
      .map(piece => {
        const value = numeric(piece);

        if (!Number.isFinite(value)) {
          return piece;
        }

        const cm = Math.round(value * 25.4) / 10;

        return Number.isInteger(cm)
          ? String(cm)
          : cm.toFixed(1);
      })
      .join('–');
  }

  /* ==========================================
     2. TAB HANDLING
     ========================================== */

  function activateTab(tab) {
    const guide = tab.closest('.ibkul-kids-guide');
    if (!guide) return;

    const panelId = tab.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);

    if (!panel || !guide.contains(panel)) return;

    guide.querySelectorAll('[data-guide-tab]')
      .forEach(item => {
        const active = item === tab;

        item.classList.toggle('is-active', active);
        item.setAttribute(
          'aria-selected',
          String(active)
        );
        item.tabIndex = active ? 0 : -1;
      });

    guide.querySelectorAll('[data-guide-panel]')
      .forEach(item => {
        item.hidden = item !== panel;
      });
  }

  /* ==========================================
     3. UNIT SELECTOR
     ========================================== */

  function activateUnit(button) {
    const guide = button.closest('.ibkul-kids-guide');
    if (!guide) return;

    const unit = button.dataset.unit;

    if (unit !== 'in' && unit !== 'cm') return;

    guide.querySelectorAll('[data-unit]')
      .forEach(item => {
        const active = item === button;

        item.classList.toggle('is-active', active);
        item.setAttribute(
          'aria-pressed',
          String(active)
        );
      });

    guide.querySelectorAll('[data-measure-in]')
      .forEach(cell => {
        const original = cell.dataset.measureIn;

        cell.textContent = unit === 'in'
          ? original
          : toCm(original);
      });
  }

  /* ==========================================
     4. DYNAMIC HOW TO MEASURE
     ========================================== */

  function formatMeasureInstructions() {
    document.querySelectorAll(
      '.ibkul-kids-guide__instructions ' +
      '.metafield-rich_text_field'
    ).forEach(container => {

      if (container.dataset.formatted === 'true') {
        return;
      }

      /*
       * Shopify Rich Text can generate:
       *
       * <p>
       *   <strong>Chest</strong><br>
       *   Measure around the chest.<br><br>
       *   <strong>Front HPS Length</strong><br>
       *   Measure from shoulder to hem.
       * </p>
       *
       * We detect each <strong> label dynamically.
       */

      const labels = Array.from(
        container.querySelectorAll('strong, b')
      );

      if (!labels.length) return;

      const groups = [];

      labels.forEach((labelElement, index) => {
        const label = labelElement.textContent.trim();

        if (!label) return;

        const start = labelElement;
        const next = labels[index + 1] || null;

        let description = '';
        let node = start.nextSibling;

        while (node) {
          if (next && node === next) break;

          if (node.nodeType === Node.TEXT_NODE) {
            description += node.textContent;
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches('strong, b')) break;

            if (node.tagName === 'BR') {
              description += '\n';
            } else {
              description += node.textContent;
            }
          }

          node = node.nextSibling;
        }

        description = description
          .replace(/\s+/g, ' ')
          .trim();

        if (description) {
          groups.push({ label, description });
        }
      });

      if (!groups.length) return;

      const wrapper = document.createElement('dl');
      wrapper.className = 'ibkul-measure-list';

      groups.forEach(group => {
        const item = document.createElement('div');
        item.className = 'ibkul-measure-item';

        const term = document.createElement('dt');
        term.textContent = group.label;

        const description = document.createElement('dd');
        description.textContent = group.description;

        item.append(term, description);
        wrapper.appendChild(item);
      });

      container.replaceChildren(wrapper);
      container.dataset.formatted = 'true';
    });
  }

  /* ==========================================
     5. EVENT HANDLERS
     ========================================== */

  document.addEventListener('click', function (event) {
    const tab = event.target.closest(
      '.ibkul-kids-guide [data-guide-tab]'
    );

    if (tab) {
      activateTab(tab);
      return;
    }

    const unitButton = event.target.closest(
      '.ibkul-kids-guide [data-unit]'
    );

    if (unitButton) {
      activateUnit(unitButton);
    }
  });

  document.addEventListener('keydown', function (event) {
    const tab = event.target.closest(
      '.ibkul-kids-guide [data-guide-tab]'
    );

    if (
      !tab ||
      !['ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      return;
    }

    const tabs = Array.from(
      tab.closest('.ibkul-kids-guide')
        .querySelectorAll('[data-guide-tab]')
    );

    if (!tabs.length) return;

    const index = tabs.indexOf(tab);

    const nextIndex = (
      index +
      (event.key === 'ArrowRight' ? 1 : tabs.length - 1)
    ) % tabs.length;

    event.preventDefault();

    tabs[nextIndex].focus();
    activateTab(tabs[nextIndex]);
  });

  /* ==========================================
     6. INITIALIZATION
     ========================================== */

  function initialize() {
    formatMeasureInstructions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    initialize
  );

})();
